import { isNull } from '@mbfab/shared'
import type { Dispatch, SetStateAction } from 'react'
import { useState } from 'react'
import type { ZodType } from 'zod'

// v1 の @mbfab/frontend-common#useStateWithLocalStorage を client へ移植したもの。
// frontend-common は antd/@dnd-kit に依存するパッケージ全体を workspace 依存に加えることになり、
// antd を持たない client の方針と衝突するため、React + zod のみで完結するこのフックだけを複製する
// （印刷設定・ズーム倍率など、v1 と同じ「保存して次回も使い回す」体験を移植するために必要）。
// schema は必須にしている。任意にすると schema 未指定時に unknown を T へ `as T` でキャストする
// 必要が生じ、typescript.md の型安全ルール（as any/as unknown 禁止）と相性が悪いため
export function useLocalStorageState<T>(
  key: string,
  initialValue: T,
  schema: ZodType<T>,
): [T, Dispatch<SetStateAction<T>>] {
  const restored = (): T => {
    try {
      const raw = localStorage.getItem(key)
      if (isNull(raw)) {
        return initialValue
      }
      const parsed: unknown = JSON.parse(raw)
      const result = schema.safeParse(parsed)
      if (!result.success) {
        localStorage.removeItem(key)
        return initialValue
      }
      return result.data
    } catch {
      return initialValue
    }
  }

  const [value, setValue] = useState<T>(restored)

  const setValueAndPersist: Dispatch<SetStateAction<T>> = (next) => {
    setValue((previous) => {
      const resolved = next instanceof Function ? next(previous) : next
      try {
        localStorage.setItem(key, JSON.stringify(resolved))
      } catch {
        // 保存に失敗しても画面上の状態は維持する（v1 と同じ: 握りつぶして良い前提）
      }
      return resolved
    })
  }

  return [value, setValueAndPersist]
}
